import { useCallback, useState } from "react";
import { useSignTransaction, useSuiClient, useCurrentAccount } from "@mysten/dapp-kit";
import { Transaction } from "@mysten/sui/transactions";
import { decodePolicyAbort } from "@altheia-xyz/sui";

/**
 * One state machine for every owner action that signs a Sui tx, so the UI
 * always reflects where the tx is and refreshes chain state on success.
 *
 *   idle → signing (wallet open) ─reject→ rejected
 *                 │ signed
 *                 ▼
 *              pending (submitted) ─abort→ failed (decoded reason)
 *                 │ ok
 *                 ▼
 *              success → audit (best-effort) → onSuccess (refetch chain)
 *
 * `audit` records the action to Chronicle (D3) and must never block the action
 * on its own failure. `onSuccess` re-reads chain so the page shows live truth.
 */

export type TxPhase = "idle" | "signing" | "pending" | "success" | "failed" | "rejected";
export type TxState = { phase: TxPhase; digest?: string; reason?: string; error?: string };

function isRejection(msg: string): boolean {
  return /reject|denied by the user|user (cancel|declin)|cancell?ed|closed/i.test(msg);
}

export type TxRun = (
  tx: Transaction | (() => Transaction),
  opts?: { onSuccess?: (digest: string) => void | Promise<void>; audit?: (digest: string) => void | Promise<void> },
) => Promise<{ ok: boolean; digest?: string; reason?: string }>;

export function useTxAction(): { state: TxState; run: TxRun; reset: () => void } {
  const client = useSuiClient();
  const account = useCurrentAccount();
  const { mutateAsync: signTransaction } = useSignTransaction();
  const [state, setState] = useState<TxState>({ phase: "idle" });

  const run = useCallback<TxRun>(
    async (txInput, opts) => {
      if (!account) { setState({ phase: "failed", error: "connect a wallet first" }); return { ok: false }; }
      let tx: Transaction;
      try { tx = typeof txInput === "function" ? txInput() : txInput; }
      catch (e) { setState({ phase: "failed", error: String((e as Error)?.message ?? e).slice(0, 140) }); return { ok: false }; }
      tx.setSenderIfNotSet(account.address);

      setState({ phase: "signing" });
      let bytes: string, signature: string;
      try {
        ({ bytes, signature } = await signTransaction({ transaction: tx }));
      } catch (e) {
        setState({ phase: "rejected", error: String((e as Error)?.message ?? e).slice(0, 140) });
        return { ok: false };
      }

      setState({ phase: "pending" });
      try {
        const res = await client.executeTransactionBlock({
          transactionBlock: bytes,
          signature,
          options: { showEffects: true, showEvents: true },
        });
        await client.waitForTransaction({ digest: res.digest });
        // Some wallet paths return without effects; re-fetch so a confirmed tx
        // isn't misread as failed (which would skip the audit post).
        let effects = res.effects;
        if (!effects) {
          const full = await client.getTransactionBlock({ digest: res.digest, options: { showEffects: true } });
          effects = full.effects ?? undefined;
        }
        if (effects && effects.status?.status !== "success") {
          const ab = decodePolicyAbort(effects.status?.error ?? "");
          setState({ phase: "failed", digest: res.digest, reason: ab?.reason_code, error: ab?.message ?? effects.status?.error });
          return { ok: false, digest: res.digest, reason: ab?.reason_code };
        }
        setState({ phase: "success", digest: res.digest });
        try { await opts?.audit?.(res.digest); } catch { /* audit is best-effort, never block the action */ }
        await opts?.onSuccess?.(res.digest);
        return { ok: true, digest: res.digest };
      } catch (e) {
        const msg = String((e as Error)?.message ?? e);
        if (isRejection(msg)) { setState({ phase: "rejected", error: msg.slice(0, 140) }); return { ok: false }; }
        const ab = decodePolicyAbort(e);
        setState({ phase: "failed", reason: ab?.reason_code, error: ab?.message ?? msg.slice(0, 140) });
        return { ok: false, reason: ab?.reason_code };
      }
    },
    [client, account, signTransaction],
  );

  const reset = useCallback(() => setState({ phase: "idle" }), []);
  return { state, run, reset };
}
